import React from "react";
import {
  Card,
  Box,
  Text,
  Flex,
  Loader,
  Grid,
  Button,
  ThemeIcon,
} from "@mantine/core";
import { shallow } from "zustand/shallow";
import axios from "axios";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
dayjs.extend(duration);

import { IconCheck } from "@tabler/icons-react";
import useAppStore from "./report-recipe-runner-store";
import { ReportRecipeStats } from "@components/ReportRecipe";

export default function ReportRecipeSetupList({ data }) {
  const inProgressItems = data.filter((m) => m.completed === false);
  const completedItems = data.filter((m) => m.completed === true);

  if (data.length === 0)
    return (
      <div
        style={{
          height: "100px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );

  return (
    <div>
      {inProgressItems.length > 0 && (
        <Box mb="md">
          <Text fw={600} mb="sm">
            In Progress ({inProgressItems.length})
          </Text>
          <Grid>
            {inProgressItems.map((item, i) => (
              <Grid.Col span={{ base: 12, md: 4 }} key={item.id}>
                <Item
                  item={{
                    ...item,
                    title: `In Progress Item ${i + 1}`,
                  }}
                />
              </Grid.Col>
            ))}
          </Grid>
        </Box>
      )}
      {completedItems.length > 0 && (
        <Box>
          <Text fw={600} mb="sm">
            Ready to view ({completedItems.length})
          </Text>
          <Grid>
            {completedItems.map((item, i) => (
              <Grid.Col span={{ base: 12, md: 4 }} key={item.id}>
                <Item
                  // viewNowEnabled={inProgressItems.length === 0}
                  viewNowEnabled
                  item={{
                    ...item,
                    title: `Completed Item ${i + 1}`,
                  }}
                />
              </Grid.Col>
            ))}
          </Grid>
        </Box>
      )}
    </div>
  );
}

const Item = ({ item, viewNowEnabled }) => {
  const baseColor = item.completed ? "blue" : "yellow";

  const {
    queryData,
    setReportData,
    setActiveTab,
    setTableConfig,
    setStatBlocks,
    setQueryData,
    setLocations,
    setOrgs,
    setReportDataQuery,
  } = useAppStore(
    (state) => ({
      queryData: state.query_data,
      setReportData: state.setReportData,
      setActiveTab: state.setActiveTab,
      setTableConfig: state.setTableConfig,
      setStatBlocks: state.setStatBlocks,
      setQueryData: state.setQueryData,
      setLocations: state.setLocations,
      setOrgs: state.setOrgs,
      setReportDataQuery: state.setReportDataQuery,
    }),
    shallow
  );

  const onClick = () => {
    axios
      .post(`/report-recipes/results/  `, {
        report_recipe_setup_id: item.id,
      })
      .then(({ data }) => {
        const res = data.response[0];
        const qd = populateQueryDataFromSetupQueryData(
          queryData,
          res.query_data
        );
        setReportData(res);
        setQueryData(qd);
        setReportDataQuery(qd);
        if (res.meta_block_data) {
          setStatBlocks(res.meta_block_data);
        }
        if (res.table_config) {
          setTableConfig(res.table_config);
        }
        if (res.query_data.saved_recipe_locations) {
          setLocations(res.query_data.saved_recipe_locations, true);
        }
        if (res.query_data.saved_recipe_orgs) {
          setOrgs(res.query_data.saved_recipe_orgs, true);
        }
        setActiveTab("form");

        // Scroll to reportDataRef after successful loading
        setTimeout(() => {
          const reportDataElement = document.querySelector(
            '[data-ref="reportData"]'
          );
          if (reportDataElement) {
            reportDataElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100); // Small delay to ensure DOM is updated
      })
      .catch((err) => {
        toast.error(err);
      });
  };

  const completedTime = item.completed
    ? dayjs(item.completed_at).diff(dayjs(item.started_at), "seconds")
    : 0;

  return (
    <Card
      radius="sm"
      p="md"
      style={{
        background: `var(--mantine-color-${baseColor}-1)`,
        borderColor: `var(--mantine-color-${baseColor}-3)`,
      }}
    >
      <Flex align="center" gap="xs">
        <Text fw={600}>{item.title}</Text>
        {item.completed ? (
          <ThemeIcon
            color={`var(--mantine-color-green-7)`}
            size="xs"
            radius="xl"
          >
            <IconCheck size={14} />
          </ThemeIcon>
        ) : (
          <Loader
            size="sm"
            type="dots"
            color={`var(--mantine-color-${baseColor}-7)`}
          />
        )}
      </Flex>
      <Text size="sm" mt="xs">
        Started {new Date(item.started_at).toLocaleString()}
      </Text>
      {item.completed && (
        <Text size="sm">
          Completed {new Date(item.completed_at).toLocaleString()}
        </Text>
      )}
      {!item.completed && (
        <Box mt="md">
          <ReportRecipeStats
            recipe={{
              started_at: item.started_at,
              show_elapsed: true,
              average_processing_time: item.average_processing_time,
            }}
            color="yellow"
          />
        </Box>
      )}
      {item.completed && (
        <Box mt="md">
          <Box mb="md">
            <ReportRecipeStats
              recipe={{
                average_processing_time: completedTime,
                setup: true,
              }}
            />
          </Box>
          {viewNowEnabled ? (
            <Button
              size="sm"
              color={`var(--mantine-color-${baseColor}-7)`}
              onClick={onClick}
            >
              View Now
            </Button>
          ) : (
            <Text size="sm">Please allow in progress items to finish</Text>
          )}
        </Box>
      )}
    </Card>
  );
};

function populateQueryDataFromSetupQueryData(queryData, setupQueryData) {
  const qd = { ...queryData };

  // Iterate through queryData's keys and use matching keys from setupQueryData
  Object.keys(queryData).forEach((key) => {
    if (setupQueryData.hasOwnProperty(key)) {
      qd[key] = setupQueryData[key];
    }
  });

  return qd;
}
